import React, { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { v4 } from "uuid";
import { cloneDeep, debounce } from "lodash";
import { AxiosResponse } from "axios";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { Checkbox } from "primereact/checkbox";
import { ConfirmDialog } from "primereact/confirmdialog";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { PdfPreview } from "../../components/PdfPreview/PDFPreview";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { NewMercantileRequestForm } from "../../forms/NewMercantileRequestForm/NewMercantileRequestForm";
import { RoleType } from "../../models/Role";
import { PartnerType } from "../../models/Partner";
import { Request, RequestShort, RequestType } from "../../models/Request";
import { Product, ProductType, ProductCategory } from "../../models/Product";
import { Utility } from "../../core/Utility";
import { activeUser } from "../../services/AuthService";
import { useApplicationContext } from "../../application/ApplicationContext";
import { getRequestTemplate } from "../../document-templates/RequestTemplate";
import {
  getAllRequests,
  getOneRequest,
  updateOneRequest,
  deleteOneRequest,
} from "../../services/NewVersionService";
import { getAllProducts } from "../../services/ProductService";
import { getAllProductCategories } from "../../services/ProductCategoryService";
import "./NewMercantileRequestsPage.scss";

export const NewMercantileRequestsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useRef(null);
  const downloadPdfDocument = useRef(null);
  const printPdfDocument = useRef(null);
  const {
    state,
    selectedReqDateFromSet,
    selectedReqDateToSet,
    selectedReqProductCategorySet,
    selectedReqProductSet,
  } = useApplicationContext();

  const filteredRequestsRef = useRef<RequestShort[] | null>(null);

  const businessYear = state.businessYear ?? new Date().getFullYear();
  const yearStart = useMemo(
    () => new Date(businessYear, 0, 1, 0, 0, 1),
    [businessYear],
  );
  const yearEnd = useMemo(
    () => new Date(businessYear, 11, 31, 23, 59, 59),
    [businessYear],
  );

  const prevBusinessYearRef = useRef<number>(businessYear);
  const skipNextGlobalSyncRef = useRef(false);

  const startOfDay = (d: Date) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  };

  const endOfDay = (d: Date) => {
    const x = new Date(d);
    x.setHours(23, 59, 59, 0);
    return x;
  };

  const safeDate = (v: any): Date | null => {
    if (!v) return null;
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  };

  const clampToBusinessYear = (d: Date) =>
    Utility.clampDateToRange(d, yearStart, yearEnd);

  const getDefaultRangeForBusinessYear = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    // Tekuća godina - poslednjih 7 dana (ali ne pre početka godine)
    if (businessYear === currentYear) {
      const sevenDaysAgo = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - 7,
        0,
        0,
        1,
      );
      const from = clampToBusinessYear(startOfDay(sevenDaysAgo));
      const to = clampToBusinessYear(today);
      return { from, to };
    }
    // Bilo koja druga godina - ceo raspon
    return { from: yearStart, to: yearEnd };
  };

  const [dateFrom, setDateFrom] = useState<Date>(() => {
    const g = safeDate(state.selectedReqDateFrom);
    return g ? clampToBusinessYear(g) : getDefaultRangeForBusinessYear().from;
  });

  const [dateTo, setDateTo] = useState<Date>(() => {
    const g = safeDate(state.selectedReqDateTo);
    return g ? clampToBusinessYear(g) : getDefaultRangeForBusinessYear().to;
  });

  const [products, setProducts] = useState<Product[]>([]);
  const [productCategories, setProductCategories] = useState<ProductCategory[]>(
    [],
  );

  const [selectedProductCategory, setSelectedProductCategory] =
    useState<ProductCategory>();
  const [selectedProduct, setSelectedProduct] = useState<Product>(
    {} as Product,
  );
  const [allMercantileRequests, setAllMercantileRequests] = useState<
    RequestShort[]
  >([]);
  const [mercantileRequests, setMercantileRequests] = useState<RequestShort[]>(
    [],
  );

  const [selectedRequest, setSelectedRequest] = useState<Request>();
  const [showForm, setShowForm] = useState<string>("");

  const [deleteConfirmVisible, setDeleteConfirmVisible] =
    useState<boolean>(false);
  const [requestToDelete, setRequestToDelete] = useState<string>("");

  const [processing, setProcessing] = useState(false);
  const [refreshData, setRefreshData] = useState<boolean>(false);
  const [pdfData, setPdfData] = useState<any>();
  const [globalFilterValue, setGlobalFilterValue] = useState<string>("");
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    requestNumberFormatted: {
      value: null,
      matchMode: FilterMatchMode.CONTAINS,
    },
    year: { value: null, matchMode: FilterMatchMode.CONTAINS },
    partnerName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    place: { value: null, matchMode: FilterMatchMode.CONTAINS },
    note: { value: null, matchMode: FilterMatchMode.CONTAINS },
    amount: { value: null, matchMode: FilterMatchMode.CONTAINS },
    type: { value: null, matchMode: FilterMatchMode.EQUALS },
    transportCost: { value: null, matchMode: FilterMatchMode.EQUALS },
    isFinished: { value: null, matchMode: FilterMatchMode.EQUALS },
  });

  const debouncedSetFilters = useMemo(
    () =>
      debounce((nextFilters: typeof filters) => {
        setFilters(nextFilters);
      }, 300),
    [],
  );

  const debouncedSetFilteredRequestsRef = useMemo(
    () =>
      debounce((filteredData: RequestShort[]) => {
        filteredRequestsRef.current = filteredData;
      }, 300),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSetFilters.cancel();
      debouncedSetFilteredRequestsRef.cancel();
    };
  }, [debouncedSetFilters, debouncedSetFilteredRequestsRef]);

  useEffect(() => {
    (async () => {
      try {
        setProcessing(true);
        const unwrapArray = (resp: any): any[] => {
          const d = resp?.data;
          if (d?.result && Array.isArray(d.result)) return d.result;
          if (Array.isArray(d)) return d;
          return [];
        };

        const [prodResp, catResp] = await Promise.all([
          getAllProducts(),
          getAllProductCategories(),
        ]);

        const pr = unwrapArray(prodResp) as Product[];
        const ca = unwrapArray(catResp) as ProductCategory[];

        setProducts(pr);
        setProductCategories(ca);

        if (state.selectedReqProductCategory) {
          // ako je u global state-u ceo objekat
          setSelectedProductCategory(state.selectedReqProductCategory as any);
        }
        if (state.selectedReqProduct) {
          setSelectedProduct(state.selectedReqProduct as any);
        }
      } catch (e) {
        console.error(e);
        // @ts-ignore
        toast.current?.show({
          severity: "error",
          detail: t("Došlo je do greške pri učitavanju šifarnika"),
          life: 3000,
        });
      } finally {
        setProcessing(false);
      }
    })();
  }, []);

  useEffect(() => {
    filteredRequestsRef.current = mercantileRequests;
  }, [mercantileRequests]);

  useEffect(() => {
    formatMercantileRequestsData(allMercantileRequests);
    resetFilters();
  }, [allMercantileRequests, selectedProduct, selectedProductCategory]);

  useEffect(() => {
    const prev = prevBusinessYearRef.current;
    if (prev === businessYear) return;

    prevBusinessYearRef.current = businessYear;

    const { from, to } = getDefaultRangeForBusinessYear();

    skipNextGlobalSyncRef.current = true;

    setDateFrom(from);
    setDateTo(to);
    selectedReqDateFromSet(from);
    selectedReqDateToSet(to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessYear, yearStart, yearEnd]);

  useEffect(() => {
    if (skipNextGlobalSyncRef.current) {
      skipNextGlobalSyncRef.current = false;
      return;
    }

    const gFrom = safeDate(state.selectedReqDateFrom);
    const gTo = safeDate(state.selectedReqDateTo);

    const hasGlobal = !!gFrom && !!gTo;

    const globalValidForYear =
      hasGlobal &&
      gFrom! >= yearStart &&
      gFrom! <= yearEnd &&
      gTo! >= yearStart &&
      gTo! <= yearEnd;

    if (globalValidForYear) {
      const fixedFrom = gFrom! > gTo! ? gTo! : gFrom!;
      const fixedTo = gFrom! > gTo! ? gFrom! : gTo!;
      setDateFrom(fixedFrom);
      setDateTo(fixedTo);
      return;
    }

    const { from, to } = getDefaultRangeForBusinessYear();
    setDateFrom(from);
    setDateTo(to);
    selectedReqDateFromSet(from);
    selectedReqDateToSet(to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearStart, yearEnd, state.selectedReqDateFrom, state.selectedReqDateTo]);

  useEffect(() => {
    setProcessing(true);

    const from = startOfDay(dateFrom);
    const to = endOfDay(dateTo);

    getAllRequests(true, from, to)
      .then((response: AxiosResponse) => {
        const allRequests = response.data.result;
        setAllMercantileRequests(allRequests);
        formatMercantileRequestsData(allRequests);
        resetFilters();
      })
      .catch(() => console.log("error"))
      .finally(() => setProcessing(false));
  }, [refreshData, dateFrom, dateTo]);

  const formatMercantileRequestsData = (allRequests: RequestShort[]) => {
    let copyOfMercantileRequests: RequestShort[] = [];

    for (const oneRequest of allRequests) {
      //filtriranje zahteva po kategoriji i vrsti robe
      if (
        //privremeno onemoguceno ogranicenje da komercijalista moze videti zahteve za samo njemu dodeljene klijente
        //ogranicenje je u polju partnerCommercialist za svaki zahtev
        // (activeUser?.role?.name !== RoleType.Commercialist ||
        //   (activeUser?.role?.name === RoleType.Commercialist &&
        //   (activeUser?.firstName + " " + activeUser?.lastName == oneRequest.partnerCommercialist))) &&
        (selectedProductCategory?.id
          ? oneRequest.categoryId == parseInt(selectedProductCategory.id)
          : true) && //zahtevi za izabranu grupu robe (default za sve)...
        (selectedProduct?.id
          ? oneRequest.productId == selectedProduct.id
          : true) //zahtevi za izabranu robu (default za sve)...
      ) {
        copyOfMercantileRequests.push(oneRequest);
      }
    }
    copyOfMercantileRequests.sort((a: RequestShort, b: RequestShort) => {
      return a.date < b.date ? 1 : -1;
    });
    setMercantileRequests(copyOfMercantileRequests);
  };

  const resetFilters = () => {
    //reset all other filters
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      requestNumberFormatted: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS,
      },
      year: { value: null, matchMode: FilterMatchMode.CONTAINS },
      partnerName: { value: null, matchMode: FilterMatchMode.CONTAINS },
      place: { value: null, matchMode: FilterMatchMode.CONTAINS },
      note: { value: null, matchMode: FilterMatchMode.CONTAINS },
      amount: { value: null, matchMode: FilterMatchMode.CONTAINS },
      type: { value: null, matchMode: FilterMatchMode.EQUALS },
      transportCost: { value: null, matchMode: FilterMatchMode.EQUALS },
      isFinished: { value: null, matchMode: FilterMatchMode.EQUALS },
    });
    setGlobalFilterValue("");
  };

  const onGlobalFilterChange = (e: any) => {
    const value = e.target.value;
    let _filters = { ...filters };

    _filters["global"].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const typeOptions = [
    { label: t("Prijem"), value: RequestType.Import },
    { label: t("Prodaja"), value: RequestType.Export },
    { label: t("Premeštaj"), value: RequestType.Relocation },
  ];

  const transportOptions = [
    { label: t("Da"), value: true },
    { label: t("Ne"), value: false },
  ];

  const typeItemTemplate = (option: any) => {
    return (
      <div className="p-multiselect-representative-option">
        <span className="image-text">{option.label}</span>
      </div>
    );
  };

  const transportItemTemplate = (option: any) => {
    return (
      <div className="p-multiselect-representative-option">
        <span className="image-text">{option.label}</span>
      </div>
    );
  };

  const typeRowFilterTemplate = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        options={typeOptions}
        onChange={(e) => {
          options.filterApplyCallback(e.value);
        }}
        itemTemplate={typeItemTemplate}
        placeholder="Izaberite"
        className="p-column-filter"
        showClear
      />
    );
  };

  const transportRowFilterTemplate = (options: any) => {
    return (
      <Dropdown
        value={options.value}
        options={transportOptions}
        onChange={(e) => {
          options.filterApplyCallback(e.value);
        }}
        itemTemplate={transportItemTemplate}
        placeholder="Izaberite"
        className="p-column-filter"
        showClear
      />
    );
  };

  const onDateFromChange = (_property: string, value: Date | null) => {
    const fallback = getDefaultRangeForBusinessYear().from;
    const raw = value ?? fallback;

    let d = clampToBusinessYear(raw);
    if (d > dateTo) d = dateTo;

    setDateFrom(d);
    selectedReqDateFromSet(d);
  };

  const onDateToChange = (_property: string, value: Date | null) => {
    const fallback = getDefaultRangeForBusinessYear().to;
    const raw = value ?? fallback;

    let d = clampToBusinessYear(raw);
    if (d < dateFrom) d = dateFrom;

    setDateTo(d);
    selectedReqDateToSet(d);
  };

  const handleProductCategoryChange = (prop: string, value: any) => {
    const copyOfNewProductCategory = productCategories.find(
      (item) => item.id == value,
    ) as ProductCategory;
    setSelectedProductCategory(copyOfNewProductCategory);
    selectedReqProductCategorySet(copyOfNewProductCategory);
    setSelectedProduct({} as Product);
    selectedReqProductSet({} as Product);
  };

  const handleProductChange = (prop: string, value: any) => {
    let copyOfNewProduct = cloneDeep(selectedProduct);
    copyOfNewProduct = products.find((item) => item.id == value) as Product;
    setSelectedProduct(copyOfNewProduct);
    selectedReqProductSet(copyOfNewProduct);
  };

  const accept = () => {
    setProcessing(true);
    deleteOneRequest(requestToDelete)
      .then(() => setRequestToDelete(""))
      .then(() => {
        const from = startOfDay(dateFrom);
        const to = endOfDay(dateTo);
        return getAllRequests(true, from, to).then(
          (response: AxiosResponse) => {
            const allRequests = response.data.result;
            setAllMercantileRequests(allRequests);
            formatMercantileRequestsData(allRequests);
            resetFilters();
          },
        );
      })
      .finally(() => setProcessing(false));
    //@ts-ignore
    toast.current.show({
      severity: "success",
      detail: `${t("Zahtev je obrisan")}`,
      life: 3000,
    });
  };

  const exportExcel = (request: Request) => {
    const excelMercantileRequestObject = {
      A: `${request.product?.name ?? ""}`,
      B: `${`${request.amount} kg`}`,
    };
    const excelObject = [
      { A: "", B: "" },
      { A: "RWA SRBIJA d.o.o.", B: "" },
      { A: "21201 Rumenka", B: "" },
      { A: "Kisački put 3G", B: "" },
      { A: "PIB: 105528683, MB 20405112", B: "" },
      { A: "", B: "" },
      {
        A: "",
        B: `Narudžbenica (broj ${request?.requestNumberFormatted}) - Dispozicija`,
      },
      {
        A: "Datum",
        B: `${Utility.formatDate(new Date(request.date as Date))}`,
      },
      { A: "Naziv kupca", B: `${request.partner?.name}` },
      { A: "Adresa kupca", B: `${request.partner?.address}` },
      { A: "PIB kupca", B: `${request.partner?.vatNumber}` },
      {
        A: "MB kupca",
        B: `${
          request.partner?.type == PartnerType.Company
            ? request.partner.companyNumber
            : request.partner?.personalCodeNumber
        }`,
      },
      { A: "Mesto utovara", B: `${request.place}` },
      { A: "", B: "" },
      { A: "Roba", B: "Količina" },
      { ...excelMercantileRequestObject },
    ];
    import("xlsx").then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(excelObject);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      saveAsExcelFile(excelBuffer, `Zahtev za rad - ${new Date().getTime()}`);
    });
  };

  const saveAsExcelFile = (buffer: any, fileName: any) => {
    import("file-saver").then((module: any) => {
      if (module && module.default) {
        let EXCEL_TYPE =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        let EXCEL_EXTENSION = ".xlsx";
        const data = new Blob([buffer], {
          type: EXCEL_TYPE,
        });
        module.default.saveAs(data, fileName + EXCEL_EXTENSION);
      }
    });
  };

  const exportToExcel = () => {
    // Export trenutnog prikaza tabele (filtrirano/sortirano)
    const source =
      filteredRequestsRef.current !== null
        ? filteredRequestsRef.current
        : mercantileRequests;

    // Sort po datumu (najstariji -> najnoviji)
    const sorted = [...source].sort((a, b) => {
      const dateA = new Date(a.date as any).getTime();
      const dateB = new Date(b.date as any).getTime();
      if (dateA !== dateB) return dateA - dateB;
      return (a.requestNumberFormatted ?? "").localeCompare(
        b.requestNumberFormatted ?? "",
      );
    });

    const excelRows = sorted.map((item: RequestShort) => {
      return {
        Tip:
          item.type === RequestType.Import
            ? t("Prijem")
            : item.type === RequestType.Export
              ? t("Prodaja")
              : t("Premeštaj"),
        BrojZahteva: item.requestNumberFormatted ?? "",
        Datum: item.date ? Utility.formatDate(new Date(item.date as any)) : "",
        Roba:
          products?.find((p) => p.id == item.productId)?.name ??
          (item as any)?.productName ??
          "",
        Kolicina: item.amount ?? "",
        Napomena: (item as any).note ?? "",
      };
    });

    const excelObject = [
      {
        Tip: "",
        BrojZahteva: "",
        Datum: "",
        Roba: "",
        Kolicina: "",
        Napomena: "",
      },
      ...excelRows,
    ];

    import("xlsx").then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(excelObject);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      saveAsExcelFile(
        excelBuffer,
        `${t("Zahtevi merkantile")}_${Utility.formatDate(new Date())}`,
      );
    });
  };

  const {
    control,
    formState: { errors },
    setValue,
  } = useForm({
    mode: "all",
  });

  const renderHeader = () => {
    return (
      <div className="search-container">
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            placeholder="Globalna pretraga..."
          />
        </span>
      </div>
    );
  };

  const formatDate = (rowData: RequestShort) => {
    return Utility.formatDate(new Date(rowData.date));
  };

  const formatType = (rowData: RequestShort) => {
    const type = rowData.type;
    const isDispositions = rowData.isDispositions;
    if (type === RequestType.Import) {
      return (
        <span
          style={isDispositions ? { color: "#00A553" } : { color: "#DC3545" }}
        >
          {t("Prijem")}
        </span>
      );
    }
    if (type === RequestType.Export) {
      return (
        <span
          style={isDispositions ? { color: "#00A553" } : { color: "#DC3545" }}
        >
          {t("Prodaja")}
        </span>
      );
    }
    if (type === RequestType.Relocation) {
      return (
        <span
          style={isDispositions ? { color: "#00A553" } : { color: "#DC3545" }}
        >
          {t("Premeštaj")}
        </span>
      );
    }
  };

  const formatProductName = (rowData: RequestShort) => {
    return products?.find((oneProduct) => oneProduct.id == rowData.productId)
      ?.name;
  };

  const formatTransportCost = (rowData: RequestShort) => {
    const cost = rowData.transportCost;
    return <span>{cost === true ? t("Da") : t("Ne")}</span>;
  };

  const formatOptions = (rowData: RequestShort) => {
    return (
      <div className="action-buttons">
        <ButtonInput
          addStyle={{ border: "none", background: "#198754" }}
          labelText={""}
          icon="bi bi-plus-square"
          onButtonClick={() => {
            const id = rowData.id;
            navigate(`/mercantile-request-details-fast/${id}`);
          }}
          disabled={
            activeUser?.role?.name === RoleType.Commercialist ||
            activeUser?.role?.name === RoleType.CommercialistII
          }
        />
        <ButtonInput
          addStyle={{ border: "none", background: "#198754" }}
          labelText={""}
          icon="bi bi-pencil"
          onButtonClick={() => {
            const isDispositions = rowData.isDispositions;
            if (isDispositions === true) {
              //@ts-ignore
              toast.current.show({
                severity: "error",
                detail: t(
                  `Nije moguće menjati zahteve za koje postoje već unete dispozicije`,
                ),
                life: 3000,
              });
            } else {
              const id = rowData.id;
              getOneRequest(id).then((resp) => {
                const req = resp?.data?.result;
                if (req?.date) req.date = new Date(req.date);
                setSelectedRequest(req);
                setShowForm(v4());
              });
            }
          }}
          disabled={activeUser?.role?.name === RoleType.Director}
        />
        <ButtonInput
          addStyle={{ border: "none", background: "#198754" }}
          labelText={""}
          icon="bi bi-file-earmark-excel"
          onButtonClick={() => {
            const id = rowData.id;
            getOneRequest(id).then((resp) => {
              const request = resp?.data?.result as Request;
              if (request?.date) request.date = new Date(request.date as any);
              exportExcel(request);
            });
          }}
        />
        <ButtonInput
          addStyle={{ border: "none", background: "#198754" }}
          labelText={""}
          icon="bi bi-printer"
          onButtonClick={() => {
            const id = rowData.id;
            getOneRequest(id)
              .then((resp) => {
                const request = resp?.data?.result as Request;
                if (request?.date) request.date = new Date(request.date as any);
                const newPdf = getRequestTemplate(request);
                setPdfData(newPdf);
              })
              .then(() => {
                setTimeout(() => {
                  if (printPdfDocument.current) {
                    // @ts-ignore
                    printPdfDocument.current();
                  }
                }, 700);
              });
          }}
        />
        <ButtonInput
          addStyle={{ border: "none", background: "#198754" }}
          labelText={""}
          icon="bi bi-trash"
          onButtonClick={async () => {
            const isDispositions = rowData.isDispositions;
            if (isDispositions === true) {
              //@ts-ignore
              toast.current.show({
                severity: "error",
                detail: t(
                  `Nije moguće brisati zahteve za koje postoje već unete dispozicije`,
                ),
                life: 3000,
              });
            } else {
              const id = rowData.id;
              setRequestToDelete(id);
              setDeleteConfirmVisible(true);
            }
          }}
          disabled={
            activeUser?.role?.name !== RoleType.Administrator &&
            activeUser?.role?.name !== RoleType.Superadmin
          }
        />
      </div>
    );
  };

  const formatIsRequestFinished = (rowData: RequestShort) => {
    return (
      <div className="completed-button">
        <Checkbox
          id="isRequestFinished"
          checked={rowData.isRequestFinished === true}
          onChange={(e) => {
            const id = rowData.id;
            setProcessing(true);
            // minimalni patch: samo completedRequest (backend update radi merge)
            updateOneRequest(id, { completedRequest: e.checked })
              .then(() => {
                // refresh liste
                const from = startOfDay(dateFrom);
                const to = endOfDay(dateTo);
                return getAllRequests(true, from, to).then(
                  (response: AxiosResponse) => {
                    const allRequests = response.data.result;
                    setAllMercantileRequests(allRequests);
                    formatMercantileRequestsData(allRequests);
                    resetFilters();
                  },
                );
              })
              .then(() => {
                //@ts-ignore
                toast.current?.show({
                  severity: "success",
                  detail: `${t("Zahtev uspešno izmenjen")}`,
                  life: 3000,
                });
              })
              .catch((err) => {
                console.error(err);
                //@ts-ignore
                toast.current?.show({
                  severity: "error",
                  detail: t("Došlo je do greške pri snimanju zahteva"),
                  life: 3000,
                });
              })
              .finally(() => setProcessing(false));
          }}
        />
      </div>
    );
  };

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-truck header-title-icon"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
              }}
            ></i>
            <span className="header-left-title">
              {t("Lista kreiranih zahteva merkantile")}
            </span>
            <span className="header-elements-text">{`${t("od")}`}</span>
            <DateInput
              id="dateFrom"
              value={dateFrom}
              onValueChange={onDateFromChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
            <span className="header-elements">{`${t("do")}`}</span>
            <DateInput
              id="dateTo"
              value={dateTo}
              onValueChange={onDateToChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
            <span className="header-elements">{`${t("za")}`}</span>
            <DropdownInput
              id="productCategory"
              value={selectedProductCategory?.id}
              onValueChange={handleProductCategoryChange}
              optionValue="id"
              optionLabel="name"
              options={productCategories.filter(
                (item) => item.type == ProductType.MercantileGoods,
              )}
              filter={false}
              labelText=""
              placeholderText={t("vrstu robe")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={processing}
            />
            <span className="header-elements"> </span>
            <DropdownInput
              id="selectedProduct"
              value={selectedProduct?.id}
              onValueChange={handleProductChange}
              optionValue="id"
              optionLabel="name"
              options={products.filter(
                (item) => item.category?.id === selectedProductCategory?.id,
              )}
              filter={false}
              labelText=""
              placeholderText={t("Ime")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
              disabled={selectedProductCategory === undefined}
            />
            <ButtonInput
              addStyle={{
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                margin: "0 0 0 1rem",
              }}
              labelText={"reset"}
              icon="bi bi-arrow-repeat"
              onButtonClick={() => {
                //reset local states
                const { from, to } = getDefaultRangeForBusinessYear();
                setDateFrom(from);
                setDateTo(to);
                setSelectedProductCategory(undefined);
                setSelectedProduct({} as Product);
                //reset global states
                selectedReqDateFromSet(from);
                selectedReqDateToSet(to);
                selectedReqProductCategorySet(undefined);
                selectedReqProductSet(undefined);
              }}
              disabled={processing}
            />
          </div>
          <div className="header-right">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                marginRight: "8px",
              }}
              labelText={""}
              icon="bi bi-file-earmark-excel"
              onButtonClick={exportToExcel}
              disabled={processing === true}
            />
            <NewMercantileRequestForm
              buttonLabel={`${t("Zahtev merkantile")}`}
              buttonIcon="bi bi-truck"
              buttonStyle={{
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                marginRight: "1rem",
                margin: "0 1rem 0 0",
              }}
              show={showForm}
              refreshingData={() => setRefreshData(!refreshData)}
              request={selectedRequest}
            />
          </div>
        </div>
        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="table-body">
            <DataTable
              scrollable={true}
              scrollHeight="80vh"
              sortMode="single"
              removableSort
              filters={filters}
              filterDisplay="row"
              filterDelay={300}
              globalFilterFields={[
                "year",
                "requestNumberFormatted",
                "partnerName",
                "place",
                "note",
                "amount",
              ]}
              header={renderHeader()}
              emptyMessage="Nema zahteva merkantile za zadate kriterijume"
              value={mercantileRequests}
              onFilter={(e) => {
                debouncedSetFilters(cloneDeep(e.filters as typeof filters));
              }}
              onValueChange={(filteredData) => {
                debouncedSetFilteredRequestsRef(
                  cloneDeep((filteredData ?? []) as RequestShort[]),
                );
              }}
              showGridlines
              stripedRows
            >
              <Column
                body={formatType}
                filter
                filterField={"type"}
                showFilterMenu={false}
                filterElement={typeRowFilterTemplate}
                style={{ minWidth: "150px" }}
                header={t("Tip")}
              />
              <Column
                field="requestNumberFormatted"
                header={t("Broj zahteva")}
                sortable
                filter
                style={{ minWidth: "110px" }}
              />
              <Column
                body={formatDate}
                header={t("Datum")}
                style={{ minWidth: "80px" }}
              />
              <Column
                body={formatProductName}
                header={t("Roba")}
                style={{ minWidth: "100px" }}
              />
              <Column
                field="year"
                filter
                sortable
                header={t("Rod")}
                style={{ minWidth: "80px" }}
              />
              <Column
                field="partnerName"
                sortable
                filter
                header={t("Vlasnik robe")}
                style={{ minWidth: "100px" }}
              />
              <Column
                field="place"
                filter
                sortable
                header={t("Polazno mesto")}
                style={{ minWidth: "90px" }}
              />
              <Column
                field="note"
                filter
                header={t("Napomena")}
                style={{ minWidth: "90px" }}
              />
              <Column
                field="amount"
                filter
                sortable
                header={t("Najavljena količina")}
                style={{ minWidth: "80px" }}
              />
              <Column
                body={formatTransportCost}
                filter
                filterField={"transportCost"}
                showFilterMenu={false}
                filterElement={transportRowFilterTemplate}
                style={{ minWidth: "150px" }}
                header={t("Trošak prevoza RWA")}
              />
              <Column
                body={formatOptions}
                header={t("Opcije")}
                style={{ minWidth: "200px" }}
              />
              <Column
                body={formatIsRequestFinished}
                header={t("Zahtev obrađen")}
                style={{ minWidth: "100px" }}
              />
            </DataTable>
          </div>
        )}
        <div className="document-preview">
          <PdfPreview
            pdfDefinition={pdfData}
            downloadDocument={downloadPdfDocument}
            printDocument={printPdfDocument}
            documentName={`${t("Zahtev za obradu_")}${new Date().getTime()}`}
          />
        </div>
        <ConfirmDialog
          visible={deleteConfirmVisible}
          onHide={() => setDeleteConfirmVisible(false)}
          message={t("Da li ste sigurni da želite da obrišete zahtev?")}
          header={t("Potvrda brisanja zahteva")}
          icon="bi bi-exclamation-circle"
          acceptLabel={`${t("Da")}`}
          acceptClassName="accept-button"
          accept={accept}
          rejectLabel={`${t("Ne")}`}
          rejectClassName="reject-button"
          reject={() => {}}
        />
      </div>
    </PageLayout>
  );
};
